/**
 * BlogCard Component
 * Purpose: Displays individual blog post in a card format with animations
 */

import React from 'react';

const BlogCard = ({ blog, onEdit, onDelete, showActions = false, index = 0 }) => {
  // Category colors
  const categoryColors = {
    Career: 'bg-gradient-to-r from-blue-500 to-blue-600 text-white',
    Finance: 'bg-gradient-to-r from-green-500 to-green-600 text-white',
    Travel: 'bg-gradient-to-r from-purple-500 to-purple-600 text-white'
  };

  return (
    <div 
      className="bg-white rounded-xl shadow-lg overflow-hidden hover-lift transition-smooth animate-fadeIn"
      style={{ animationDelay: `${index * 0.1}s` }}
    >
      {/* Blog Image with Overlay */}
      {blog.image ? (
        <div className="relative h-48 overflow-hidden group">
          <img
            src={blog.image}
            alt={blog.title}
            className="w-full h-full object-cover transform group-hover:scale-110 transition-smooth"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent opacity-0 group-hover:opacity-100 transition-smooth"></div>
        </div>
      ) : (
        <div className="h-48 bg-gradient-to-br from-blue-400 via-purple-400 to-pink-400 flex items-center justify-center">
          <span className="text-6xl">📝</span>
        </div>
      )}

      <div className="p-6">
        {/* Category Badge with Animation */}
        <span className={`inline-block ${categoryColors[blog.category] || 'bg-gray-500 text-white'} text-xs px-4 py-1.5 rounded-full mb-3 font-semibold shadow-md hover-scale`}>
          {blog.category}
        </span>

        {/* Blog Title with Hover Effect */}
        <h3 className="text-xl font-bold text-gray-800 mb-2 hover:text-blue-600 transition-smooth cursor-pointer">
          {blog.title}
        </h3>

        {/* Blog Content (truncated) */}
        <p className="text-gray-600 mb-4 line-clamp-3 leading-relaxed">
          {blog.content}
        </p>

        {/* Blog Metadata with Icons */}
        <div className="flex justify-between items-center text-sm text-gray-500 mb-4 pb-4 border-b border-gray-100">
          <span className="flex items-center gap-1">
            <span className="text-base">👤</span>
            <span className="font-medium">{blog.author}</span>
          </span>
          <span className="flex items-center gap-1">
            <span className="text-base">📅</span>
            <span>{new Date(blog.createdAt).toLocaleDateString()}</span>
          </span>
        </div>

        {/* Action Buttons with Enhanced Styling */}
        {showActions && (
          <div className="flex gap-3">
            <button
              onClick={() => onEdit(blog)}
              className="flex-1 bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white px-4 py-2.5 rounded-lg transition-smooth font-semibold shadow-md hover:shadow-lg transform hover:scale-105"
            >
              ✏️ Edit
            </button>
            <button
              onClick={() => onDelete(blog._id)}
              className="flex-1 bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700 text-white px-4 py-2.5 rounded-lg transition-smooth font-semibold shadow-md hover:shadow-lg transform hover:scale-105"
            >
              🗑️ Delete
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default BlogCard;
